import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Stethoscope, BookMarked, FileCheck2, Plane, PlaneTakeoff, TrendingUp, Users, ChevronRight, ChevronDown } from "lucide-react";
import api, { resolveMediaUrl } from "@/lib/api";

// ------------------------------------------------------------------
// Adelina · Live Placement Pipeline
//
// A luxury, editorial pipeline page:
//   1. TROPHY FUNNEL — real tapering funnel (each stage narrower than
//      previous), monochrome gold-on-black, giant serif counters,
//      conversion % between stages.
//   2. JOURNEY TIMELINE — for each candidate a 6-step progress dotted
//      timeline (completed / current / upcoming) so employers can see
//      exactly where each placement is right now.
// ------------------------------------------------------------------

const NON_FORMAL_STAGES = [
  { key: "selected",  icon: CheckCircle2, label: "Selected by Employer" },
  { key: "medical",   icon: Stethoscope,  label: "Medical Check-up" },
  { key: "passport",  icon: BookMarked,   label: "Passport" },
  { key: "job_order", icon: FileCheck2,   label: "Job Order" },
  { key: "visa",      icon: FileCheck2,   label: "Visa" },
  { key: "flight",    icon: Plane,        label: "Flight" },
  { key: "departed",  icon: PlaneTakeoff, label: "Departed" },
];

const FORMAL_STAGES = [
  { key: "selected",   icon: CheckCircle2, label: "Selected by Employer" },
  { key: "medical",    icon: Stethoscope,  label: "Medical Check-up" },
  { key: "psikotes",   icon: FileCheck2,   label: "Psikotes" },
  { key: "id_making",  icon: BookMarked,   label: "Pembuatan ID" },
  { key: "passport",   icon: BookMarked,   label: "Passport" },
  { key: "ld_process", icon: FileCheck2,   label: "Proses LD" },
  { key: "visa",       icon: FileCheck2,   label: "Visa" },
  { key: "job_order",  icon: FileCheck2,   label: "Job Order" },
  { key: "opp",        icon: FileCheck2,   label: "OPP · Pra Pemberangkatan" },
  { key: "flight",     icon: Plane,        label: "Flight" },
  { key: "departed",   icon: PlaneTakeoff, label: "Departed" },
];

const STAGES_BY_TYPE = { non_formal: NON_FORMAL_STAGES, formal: FORMAL_STAGES };

// Backward-compat alias — some legacy references still expect a flat `STAGES` list.
const STAGES = NON_FORMAL_STAGES;
const STAGE_INDEX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));

// Trophy taper widths (%). We pick a slice matching the active pipeline length.
const FUNNEL_WIDTHS_FULL = [100, 92, 84, 76, 68, 60, 52, 44, 36, 28, 20];

export default function Pipeline() {
  const { t } = useTranslation();
  const DEFAULT_TAB = 'non_formal';
  const [tab, setTab] = useState(DEFAULT_TAB);
  useEffect(() => {
    // If the URL contains ?type=formal we default the tab accordingly
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('type');
      if (t === 'formal' || t === 'non_formal') setTab(t);
    } catch (err) {
      // URL parse can fail on very old browsers — log for debug then keep default tab.
      console.warn("[Pipeline] URL parse failed, keeping default tab:", err);
    }
  }, []);
  const [expanded, setExpanded] = useState(null); // stage key

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["pipeline"],
    queryFn: async () => (await api.get("/pipeline")).data,
    refetchInterval: 60_000,
  });

  const activeStages = STAGES_BY_TYPE[tab] || STAGES_BY_TYPE.non_formal;
  const FUNNEL_WIDTHS = FUNNEL_WIDTHS_FULL.slice(0, activeStages.length);

  const filtered = useMemo(() => (
    tab === "all" ? rows : rows.filter((r) => (r.pipeline_type || "non_formal") === tab)
  ), [rows, tab]);

  const grouped = useMemo(() => {
    const m = Object.fromEntries(activeStages.map((s) => [s.key, []]));
    filtered.forEach((r) => { if (m[r.pipeline_stage]) m[r.pipeline_stage].push(r); });
    return m;
  }, [filtered, activeStages]);

  const stageLabel = (k) => {
    const s = activeStages.find((x) => x.key === k);
    return s ? s.label : k;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      {/* -------- Header -------- */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline mb-3" data-testid="pipeline-overline">{t("pipeline.overline", "Live Pipeline")}</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-zinc-950 tracking-tight max-w-3xl leading-[1.05]">
            {t("pipeline.title", "Candidates currently in process")}
          </h1>
          <p className="mt-5 text-zinc-700 max-w-2xl leading-relaxed text-[15px]">
            {t("pipeline.subtitle", "Real-time operations dashboard. Identity is masked for privacy — full profiles are visible only to verified employers.")}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[13px]">
          <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-zinc-200 shadow-sm">
            <Users size={14} className="text-[#b8860b]" />
            <span className="text-zinc-500 font-medium text-xs uppercase tracking-[0.15em]">{t("pipeline.total", "Total")}</span>
            <span className="font-serif text-2xl text-zinc-950 leading-none" data-testid="pipeline-total">{filtered.length}</span>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mt-10 inline-flex items-center gap-1 p-1 bg-zinc-100 rounded-full text-[12.5px] font-medium">
        {[
          { k: "all",        label: t("pipeline.tab_all", "All") },
          { k: "non_formal", label: t("pipeline.tab_non_formal", "Non-Formal") },
          { k: "formal",     label: t("pipeline.tab_formal", "Formal") },
        ].map((o) => (
          <button
            key={o.k}
            onClick={() => setTab(o.k)}
            data-testid={`pipeline-tab-${o.k}`}
            className={`px-4 py-1.5 rounded-full transition-all ${
              tab === o.k ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-600 hover:text-zinc-950"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* -------- Trophy Funnel -------- */}
      <TrophyFunnel grouped={grouped} stageLabel={stageLabel} t={t} onOpenStage={setExpanded} expanded={expanded} activeStages={activeStages} funnelWidths={FUNNEL_WIDTHS} />

      {/* -------- Journey Timelines -------- */}
      {isLoading && <div className="mt-12 text-center text-zinc-500">{t("common.loading", "Loading…")}</div>}
      {!isLoading && filtered.length === 0 && (
        <div className="mt-16 text-center border border-dashed border-zinc-300 rounded-3xl p-16 bg-zinc-50/40">
          <div className="text-zinc-500 text-sm">{t("pipeline.empty", "No candidates in the pipeline right now.")}</div>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="mt-16">
          <div className="flex items-center gap-2 mb-6">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
              {t("pipeline.journeys_label", "Individual Journeys")}
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-zinc-200 via-zinc-200 to-transparent" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-400 font-medium">
              {filtered.length} {t("pipeline.count_suffix", "in process")}
            </div>
          </div>
          <div className="space-y-4">
            {filtered.map((c) => <JourneyRow key={c.id} candidate={c} t={t} stageLabel={stageLabel} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Trophy Funnel — the hero chart
// ==================================================================
function TrophyFunnel({ grouped, stageLabel, t, onOpenStage, expanded, activeStages = STAGES, funnelWidths = FUNNEL_WIDTHS_FULL }) {
  const counts = activeStages.map((s) => (grouped[s.key] || []).length);
  const selectedCount = counts[0] || 0;

  const conversion = (i) => {
    if (i === 0) return null;
    const prev = counts[i - 1] || 0;
    const cur = counts[i] || 0;
    if (prev === 0) return null;
    return Math.round((cur / prev) * 100);
  };

  return (
    <div className="mt-10 rounded-[28px] bg-zinc-950 text-white relative overflow-hidden shadow-2xl" data-testid="pipeline-funnel">
      {/* Gold radial glow */}
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(circle, #d4a017 0%, transparent 60%)" }}
      />
      {/* Grain */}
      <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
      />

      <div className="relative p-8 md:p-12">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-[#d4a017] font-semibold mb-3">
          <TrendingUp size={13} />
          {t("pipeline.funnel_label", "Placement Funnel")}
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-white tracking-tight leading-tight">
          {selectedCount > 0
            ? `${selectedCount} ${t("pipeline.funnel_headline_dyn", `placements moving through ${activeStages.length} stages`)}`
            : t("pipeline.funnel_headline_empty", "No active placements right now")}
        </h2>

        {/* Funnel body */}
        <div className="mt-10 space-y-1">
          {activeStages.map((s, i) => {
            const list = grouped[s.key] || [];
            const count = list.length;
            const widthPct = funnelWidths[i];
            const goldAlpha = Math.max(0.28, 1 - i * 0.13); // fades as it narrows
            const conv = conversion(i);
            const Icon = s.icon;
            const isOpen = expanded === s.key;

            return (
              <div key={s.key} className="relative" data-testid={`funnel-row-${s.key}`}>
                {/* Conversion arrow between rows */}
                {conv !== null && (
                  <div className="flex justify-center py-1.5">
                    <div className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold tracking-wider text-[#d4a017]/80">
                      <span className="w-6 h-px bg-[#d4a017]/40" />
                      <span className="tabular-nums">↓ {conv}%</span>
                      <span className="w-6 h-px bg-[#d4a017]/40" />
                    </div>
                  </div>
                )}

                {/* Funnel bar — proportionally tapered */}
                <button
                  type="button"
                  onClick={() => onOpenStage(isOpen ? null : s.key)}
                  disabled={count === 0}
                  className="block mx-auto group text-left w-full disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ maxWidth: `${widthPct}%` }}
                  data-testid={`funnel-bar-${s.key}`}
                >
                  <div
                    className="relative rounded-2xl border transition-all duration-500"
                    style={{
                      background: `linear-gradient(90deg, rgba(212,160,23,${goldAlpha * 0.15}) 0%, rgba(212,160,23,${goldAlpha * 0.28}) 50%, rgba(212,160,23,${goldAlpha * 0.15}) 100%)`,
                      borderColor: `rgba(212,160,23,${goldAlpha * 0.55})`,
                      boxShadow: count > 0 ? `0 0 0 1px rgba(212,160,23,${goldAlpha * 0.15}), 0 12px 40px -12px rgba(212,160,23,${goldAlpha * 0.35})` : "none",
                    }}
                  >
                    <div className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5">
                      <div
                        className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center shrink-0 border"
                        style={{ background: `rgba(212,160,23,${goldAlpha * 0.2})`, borderColor: `rgba(212,160,23,${goldAlpha * 0.6})`, color: "#d4a017" }}
                      >
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0 flex items-baseline gap-3">
                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#d4a017]/80 font-semibold shrink-0 hidden md:inline">
                          {t("pipeline.step_label", "Step")} {i + 1}
                        </span>
                        <span className="font-serif text-lg md:text-xl text-white tracking-tight truncate">{stageLabel(s.key)}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="tabular-nums font-serif text-3xl md:text-4xl text-white leading-none">{count}</span>
                        {count > 0 && (
                          <ChevronDown
                            size={16}
                            className={`text-[#d4a017] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Inline expansion — cards with small photo + candidate name */}
                {isOpen && count > 0 && (
                  <div className="mt-3 mx-auto px-4 py-3 rounded-xl border border-[#d4a017]/25 bg-[#d4a017]/[0.06]" style={{ maxWidth: `${widthPct}%` }}>
                    <div className="flex flex-wrap gap-2">
                      {list.map((c) => {
                        const initials = (c.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                        const src = c.photo_url ? resolveMediaUrl(c.photo_url) : null;
                        return (
                          <div key={c.id} className="text-[11.5px] text-white/90 bg-white/5 border border-white/10 rounded-full pl-1 pr-3 py-1 font-medium inline-flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full overflow-hidden bg-gradient-to-br from-[#d4a017]/40 to-[#b8860b]/60 ring-1 ring-white/20 shrink-0 relative">
                              {src && (
                                <img
                                  src={src}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                                />
                              )}
                              <div className="absolute inset-0 items-center justify-center text-[9px] text-white font-bold" style={{ display: src ? "none" : "flex" }}>{initials}</div>
                            </div>
                            <span>{c.name}<span className="text-white/50 ml-1">· {c.role}</span></span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
          <div className="text-[11px] text-white/50 uppercase tracking-[0.2em] font-medium">
            {t("pipeline.updates_every", "Updates every 60 seconds")}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-white/60 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#d4a017] animate-pulse" />
            {t("pipeline.live", "LIVE")}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================================================================
// Journey Timeline — per-candidate row
// ==================================================================
function JourneyRow({ candidate: c, t, stageLabel }) {
  const candidateStages = STAGES_BY_TYPE[c.pipeline_type] || STAGES_BY_TYPE.non_formal;
  const stageIndex = Object.fromEntries(candidateStages.map((s, i) => [s.key, i]));
  const currentIdx = stageIndex[c.pipeline_stage] ?? 0;
  const isNonFormal = (c.pipeline_type || "non_formal") === "non_formal";
  const progressPct = Math.round((currentIdx / Math.max(1, candidateStages.length - 1)) * 100);
  const initials = (c.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const photoResolved = c.photo_url ? resolveMediaUrl(c.photo_url) : null;

  return (
    <div
      className="group bg-white border border-zinc-200 rounded-3xl px-6 md:px-8 py-6 md:py-7 hover:border-[#b8860b]/50 hover:shadow-lg transition-all"
      data-testid={`journey-row-${c.id}`}
    >
      {/* Identity + tags row */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* Small photo with initials fallback */}
          <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden shrink-0 ring-2 ring-[#d4a017]/20 bg-gradient-to-br from-[#d4a017]/20 to-[#b8860b]/30">
            {photoResolved && (
              <img
                src={photoResolved}
                alt=""
                className="w-full h-full object-cover object-top"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
              />
            )}
            <div
              className="absolute inset-0 items-center justify-center font-serif text-[#8a5f07] text-base md:text-lg"
              style={{ display: photoResolved ? "none" : "flex" }}
            >
              {initials}
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-serif text-2xl md:text-3xl text-zinc-950 tracking-tight leading-tight truncate">{c.name}</div>
            <div className="text-[13.5px] text-zinc-700 mt-1.5 leading-relaxed">
              {c.age ? `${c.age} · ` : ""}{c.role || ""}
              {c.country_name ? ` · ${c.country_name}` : ""}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {c.religion && (
            <span className="text-[11px] px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-semibold uppercase tracking-wider">
              {c.religion}
            </span>
          )}
          {c.nationality && (
            <span className="text-[11px] px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-700 font-semibold uppercase tracking-wider">
              {c.nationality}
            </span>
          )}
          {isNonFormal && c.employer_display_name && (
            <span className="text-[11px] px-3 py-1 rounded-full bg-[#b8860b]/10 border border-[#b8860b]/40 text-[#8a5f07] font-semibold uppercase tracking-wider">
              {t("pipeline.picked_by", "Picked by")} {c.employer_display_name}
            </span>
          )}
        </div>
      </div>

      {/* Progress bar with % */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#b8860b] via-[#d4a017] to-[#e8c250] rounded-full transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-600 font-semibold tabular-nums shrink-0">
          {progressPct}% <span className="text-zinc-400">·</span> {t("pipeline.step_of", "Step")} {currentIdx + 1}/{candidateStages.length}
        </div>
      </div>

      {/* Dynamic-length timeline (per candidate pipeline_type) */}
      <div className="relative">
        {/* Base track */}
        <div className="absolute top-5 left-5 right-5 h-px bg-zinc-200" />
        {/* Filled track — up to current stage */}
        <div
          className="absolute top-5 left-5 h-[2px] bg-gradient-to-r from-[#b8860b] to-[#d4a017] transition-all duration-700"
          style={{
            width: currentIdx === 0
              ? "0%"
              : `calc((100% - 40px) * ${currentIdx / Math.max(1, candidateStages.length - 1)})`,
          }}
        />
        <div className={`relative grid gap-1`} style={{ gridTemplateColumns: `repeat(${candidateStages.length}, minmax(0, 1fr))` }}>
          {candidateStages.map((s, i) => {
            const state = i < currentIdx ? "done" : i === currentIdx ? "current" : "upcoming";
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex flex-col items-center gap-2.5" data-testid={`journey-step-${c.id}-${s.key}`}>
                <div className="relative">
                  {state === "current" && (
                    <span className="absolute inset-0 rounded-full bg-[#d4a017]/40 animate-ping" />
                  )}
                  <div
                    className={`relative w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      state === "done"
                        ? "bg-[#b8860b] border-[#b8860b] text-white"
                        : state === "current"
                          ? "bg-white border-[#d4a017] text-[#b8860b] shadow-[0_0_0_5px_rgba(212,160,23,0.18)]"
                          : "bg-white border-zinc-300 text-zinc-400"
                    }`}
                  >
                    {state === "done"
                      ? <CheckCircle2 size={16} strokeWidth={2.5} />
                      : <Icon size={15} />
                    }
                  </div>
                </div>
                <div
                  className={`text-[11px] text-center leading-tight font-semibold px-1 ${
                    state === "current"
                      ? "text-[#8a5f07]"
                      : state === "done"
                        ? "text-zinc-800"
                        : "text-zinc-400"
                  }`}
                >
                  {stageLabel(s.key)}
                </div>
                {state === "current" && s.key === "flight" && c.flight_date && (
                  <div className="text-[10.5px] px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 font-semibold whitespace-nowrap">
                    ✈ {c.flight_date}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Current-status highlight footer */}
      <div className="mt-6 pt-5 border-t border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="relative flex w-2.5 h-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#d4a017] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#d4a017]" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              {t("pipeline.now_at", "Currently at")}
            </div>
            <div className="font-serif text-lg md:text-xl text-zinc-950 tracking-tight leading-tight">
              {stageLabel(c.pipeline_stage)}
            </div>
          </div>
        </div>
        {currentIdx < STAGES.length - 1 ? (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold">
              {t("pipeline.next_stage", "Next")}
            </div>
            <div className="text-sm text-zinc-700 font-semibold flex items-center gap-1.5 justify-end">
              {stageLabel(STAGES[currentIdx + 1].key)}
              <ChevronRight size={14} className="text-zinc-400" />
            </div>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
            <CheckCircle2 size={12} /> {t("pipeline.completed", "Placement Complete")}
          </div>
        )}
      </div>
    </div>
  );
}
